import React from 'react';
import { motion } from 'framer-motion';
import '../styles/Footer.css';

const Footer = ({ translations, language }) => {
  const footerData = {
    en: {
      tagline: 'Transforming Businesses Through Futuristic Marketing',
      quickLinks: {
        title: 'Quick Links',
        links: [
          { name: 'Home', href: '#home' },
          { name: 'About', href: '#about' },
          { name: 'Services', href: '#services' },
          { name: 'Portfolio', href: '#portfolio' },
          { name: 'Blog', href: '#blog' },
          { name: 'Contact', href: '#contact' }
        ]
      },
      services: {
        title: 'Services',
        links: [
          { name: 'SEO Optimization', href: '#services' },
          { name: 'Paid Advertising', href: '#services' },
          { name: 'Social Media Marketing', href: '#services' },
          { name: 'Email Marketing', href: '#services' },
          { name: 'Analytics & Reporting', href: '#services' }
        ]
      },
      contact: {
        title: 'Contact Info',
        email: 'codevibe01@gmail.com',
        phone: '+2001080145935',
        location: 'Cairo, Egypt'
      },
      newsletter: {
        title: 'Stay Updated',
        description: 'Get the latest marketing insights and trends delivered to your inbox.',
        placeholder: 'Enter your email',
        button: 'Subscribe'
      },
      social: {
        title: 'Follow Me',
        platforms: [
          { name: 'LinkedIn', icon: 'fab fa-linkedin-in', href: '#' },
          { name: 'Twitter', icon: 'fab fa-twitter', href: '#' },
          { name: 'Instagram', icon: 'fab fa-instagram', href: '#' },
          { name: 'YouTube', icon: 'fab fa-youtube', href: '#' },
          { name: 'Facebook', icon: 'fab fa-facebook-f', href: '#' }
        ]
      },
      copyright: '© 2024 Zezo Digital Marketing. All rights reserved.',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service'
    },
    ar: {
      tagline: 'تحويل الأعمال من خلال التسويق المستقبلي',
      quickLinks: {
        title: 'روابط سريعة',
        links: [
          { name: 'الرئيسية', href: '#home' },
          { name: 'نبذة', href: '#about' },
          { name: 'الخدمات', href: '#services' },
          { name: 'الأعمال', href: '#portfolio' },
          { name: 'المدونة', href: '#blog' },
          { name: 'التواصل', href: '#contact' }
        ]
      },
      services: {
        title: 'الخدمات',
        links: [
          { name: 'تحسين محركات البحث', href: '#services' },
          { name: 'الإعلانات المدفوعة', href: '#services' },
          { name: 'تسويق وسائل التواصل الاجتماعي', href: '#services' },
          { name: 'التسويق عبر البريد الإلكتروني', href: '#services' },
          { name: 'التحليلات والتقارير', href: '#services' }
        ]
      },
      contact: {
        title: 'معلومات التواصل',
        email: 'codevibe01@gmail.com',
        phone: '+2001080145935',
        location: 'القاهرة، مصر'
      },
      newsletter: {
        title: 'ابق محدثاً',
        description: 'احصل على أحدث رؤى واتجاهات التسويق في صندوق الوارد الخاص بك.',
        placeholder: 'أدخل بريدك الإلكتروني',
        button: 'اشترك'
      },
      social: {
        title: 'تابعني',
        platforms: [
          { name: 'LinkedIn', icon: 'fab fa-linkedin-in', href: '#' },
          { name: 'Twitter', icon: 'fab fa-twitter', href: '#' },
          { name: 'Instagram', icon: 'fab fa-instagram', href: '#' },
          { name: 'YouTube', icon: 'fab fa-youtube', href: '#' },
          { name: 'Facebook', icon: 'fab fa-facebook-f', href: '#' }
        ]
      },
      copyright: '© 2024 زيزو للتسويق الرقمي. جميع الحقوق محفوظة.',
      privacy: 'سياسة الخصوصية',
      terms: 'شروط الخدمة'
    }
  };

  const data = footerData[language];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId.replace('#', ''));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="footer">
      <div className="footer-bg">
        <div className="footer-particles">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="footer-particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`,
                animationDuration: `${10 + Math.random() * 10}s`
              }}
            ></div>
          ))}
        </div>
      </div>

      <div className="container">
        <div className="footer-content">
          <motion.div
            className="footer-brand"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="footer-logo">
              <span className="logo-text gradient-text">ZEZO</span>
            </div>
            <p className="footer-tagline">{data.tagline}</p>
            
            <div className="footer-social">
              <h4 className="social-title">{data.social.title}</h4>
              <div className="social-links">
                {data.social.platforms.map((platform, index) => (
                  <motion.a
                    key={index}
                    href={platform.href}
                    className="social-link"
                    whileHover={{ scale: 1.2, y: -3 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <i className={platform.icon}></i>
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            className="footer-links"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="footer-column">
              <h4 className="footer-title">{data.quickLinks.title}</h4>
              <ul className="footer-list">
                {data.quickLinks.links.map((link, index) => (
                  <li key={index}>
                    <motion.a
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(link.href);
                      }}
                      whileHover={{ x: 5 }}
                    >
                      {link.name}
                    </motion.a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-title">{data.services.title}</h4>
              <ul className="footer-list">
                {data.services.links.map((link, index) => (
                  <li key={index}>
                    <motion.a
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(link.href);
                      }}
                      whileHover={{ x: 5 }}
                    >
                      {link.name}
                    </motion.a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-title">{data.contact.title}</h4>
              <div className="contact-info">
                <div className="contact-item">
                  <i className="fas fa-envelope"></i>
                  <a href={`mailto:${data.contact.email}`}>{data.contact.email}</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-phone"></i>
                  <a href={`tel:${data.contact.phone}`}>{data.contact.phone}</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>{data.contact.location}</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="footer-newsletter"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <h4 className="footer-title">{data.newsletter.title}</h4>
            <p className="newsletter-description">{data.newsletter.description}</p>
            
            <form className="newsletter-form">
              <div className="newsletter-input-group">
                <input
                  type="email"
                  placeholder={data.newsletter.placeholder}
                  className="newsletter-input"
                  required
                />
                <motion.button
                  type="submit"
                  className="newsletter-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {data.newsletter.button}
                  <i className="fas fa-paper-plane"></i>
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>

        <motion.div
          className="footer-bottom"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="footer-copyright">
            <p>{data.copyright}</p>
          </div>
          <div className="footer-legal">
            <a href="#" className="legal-link">{data.privacy}</a>
            <span className="separator">|</span>
            <a href="#" className="legal-link">{data.terms}</a>
          </div>
        </motion.div>
      </div>

      <div className="footer-glow"></div>
    </footer>
  );
};

export default Footer;